import { generateJSON, type JSONContent } from '@tiptap/core';
import type { Editor } from '@tiptap/react';
import { ALLOWED_IMAGE_MIME, type UploadedImage } from './editorHelpers';

/**
 * Pasting rich content (e.g. from Google Docs or a web page) puts the images
 * in the clipboard's `text/html` payload as <img> tags — NOT as files. To keep
 * the original "text → image → text" order, the HTML is split into an ordered
 * list of segments; images that sit together on the same line are grouped into
 * a single row grid, while images on separate lines/paragraphs stay as separate
 * stacked grids. The images are uploaded through the normal Dx pipeline and the
 * whole thing is re-inserted in one ordered pass.
 *
 * Frontend-only: `data:` URIs always work; remote `https://` sources work only
 * when the host allows cross-origin fetch. CORS-blocked sources are skipped and
 * reported via the caller's notice (a backend fetch proxy would be required to
 * import those reliably).
 */

type Segment = { kind: 'html'; html: string } | { kind: 'image'; src: string } | { kind: 'break' };

const MIME_EXT: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp'
};

const MAX_GRID_COLS = 4;
const GRID_ROW_HEIGHT = 140;

/**
 * Tags that introduce a line/paragraph boundary. Images separated by one of
 * these belong to different lines and must not be merged into the same grid;
 * images separated only by inline markup (spans, links, whitespace) are on the
 * same line and get grouped.
 */
const BLOCK_TAGS = new Set([
    'ADDRESS',
    'ARTICLE',
    'ASIDE',
    'BLOCKQUOTE',
    'DD',
    'DIV',
    'DL',
    'DT',
    'FIELDSET',
    'FIGCAPTION',
    'FIGURE',
    'FOOTER',
    'FORM',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'HEADER',
    'HR',
    'LI',
    'MAIN',
    'NAV',
    'OL',
    'P',
    'PRE',
    'SECTION',
    'TABLE',
    'TBODY',
    'TD',
    'TFOOT',
    'TH',
    'THEAD',
    'TR',
    'UL'
]);

/**
 * Walk a clipboard HTML payload in document order, emitting a segment for each
 * run of non-image markup, each <img>, and each line/paragraph boundary.
 * Image-free block elements are kept verbatim to preserve their formatting.
 */
export function splitHtmlByImages(html: string): Segment[] {
    if (!html) return [];
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const segments: Segment[] = [];
    let buffer = '';

    const flushHtml = (): void => {
        if (buffer.trim().length > 0) segments.push({ kind: 'html', html: buffer });
        buffer = '';
    };

    const pushBreak = (): void => {
        flushHtml();
        const last = segments[segments.length - 1];
        if (last && last.kind !== 'break') segments.push({ kind: 'break' });
    };

    const walk = (node: Node): void => {
        node.childNodes.forEach((child) => {
            if (child.nodeType === Node.ELEMENT_NODE) {
                const el = child as Element;
                const tag = el.tagName;
                if (tag === 'IMG') {
                    const src = el.getAttribute('src');
                    if (src) {
                        flushHtml();
                        segments.push({ kind: 'image', src });
                    }
                    return;
                }
                if (tag === 'BR') {
                    pushBreak();
                    return;
                }
                if (BLOCK_TAGS.has(tag)) {
                    if (el.querySelector('img')) {
                        // A line/paragraph that contains images: bound it with breaks so
                        // its images don't merge with neighbours, then descend so its own
                        // same-line images can group together.
                        pushBreak();
                        walk(el);
                        pushBreak();
                    } else {
                        // Image-free block: keep its markup as-is to preserve formatting.
                        buffer += el.outerHTML;
                    }
                    return;
                }
                // Inline element (span, b, i, a, …): descend when it wraps an image so
                // the image stays groupable with its line; otherwise keep its markup.
                if (el.querySelector('img')) {
                    walk(el);
                } else {
                    buffer += el.outerHTML;
                }
            } else if (child.nodeType === Node.TEXT_NODE) {
                const text = child.textContent ?? '';
                if (text.trim().length > 0) buffer += text;
            }
        });
    };

    walk(doc.body);
    flushHtml();
    return segments;
}

/** True when the segment list contains at least one image to import. */
export function hasImageSegment(segments: Segment[]): boolean {
    return segments.some((s) => s.kind === 'image');
}

/** Fetch a pasted image source into a validated File, or null if it can't be imported. */
async function srcToFile(src: string): Promise<File | null> {
    try {
        // Works for both `data:` URIs (always) and `http(s)` URLs (subject to CORS).
        // `no-referrer` avoids leaking our origin to a host embedded in pasted HTML.
        const response = await fetch(src, { referrerPolicy: 'no-referrer' });
        if (!response.ok) return null;
        const blob = await response.blob();
        if (!(ALLOWED_IMAGE_MIME as readonly string[]).includes(blob.type)) return null;
        const ext = MIME_EXT[blob.type] ?? 'png';
        return new File([blob], `pasted-image.${ext}`, { type: blob.type });
    } catch {
        // Network error or CORS rejection — the source can't be imported client-side.
        return null;
    }
}

/** Evenly distributed column widths summing to 100 (mirrors the grid extension). */
function evenColWidths(cols: number): number[] {
    const base = Math.floor(100 / cols);
    const widths = Array.from({ length: cols }, () => base);
    widths[cols - 1] += 100 - base * cols;
    return widths;
}

/** Build an imageGrid node JSON laying a run of same-line images out as a row. */
function buildGridNode(images: UploadedImage[]): JSONContent {
    const cols = Math.min(images.length, MAX_GRID_COLS);
    const rows = Math.ceil(images.length / cols);
    const colWidths = evenColWidths(cols);
    const totalCells = rows * cols;
    const content = Array.from({ length: totalCells }, (_, i) => {
        const u = images[i];
        return {
            type: 'imageGridCell',
            attrs: u ? { src: u.url, fileId: u.fileId } : { src: null, fileId: null }
        };
    });
    return {
        type: 'imageGrid',
        attrs: { rows, cols, rowHeight: GRID_ROW_HEIGHT, colWidths },
        content
    };
}

/**
 * Upload every image referenced by the segments, then insert the segments —
 * text and (row-grouped) images interleaved in their original order — at the
 * paste position in a single pass. Reports how many images couldn't be imported
 * via `onNotice` so the user gets a brief, non-blocking heads-up.
 */
export async function importPastedImages(
    editor: Editor,
    segments: Segment[],
    insertAt: { from: number; to: number },
    uploadFn: (file: File) => Promise<UploadedImage>,
    onBusy: (busy: boolean) => void,
    onNotice: (message: string) => void,
    toNodes: (images: UploadedImage[]) => JSONContent[] = (images) => [buildGridNode(images)]
): Promise<void> {
    const imageSrcs = segments
        .filter((s): s is Extract<Segment, { kind: 'image' }> => s.kind === 'image')
        .map((s) => s.src);
    if (imageSrcs.length === 0) return;

    onBusy(true);
    let uploads: Array<UploadedImage | null>;
    try {
        uploads = await Promise.all(
            imageSrcs.map(async (src) => {
                const file = await srcToFile(src);
                if (!file) return null;
                try {
                    return await uploadFn(file);
                } catch {
                    return null;
                }
            })
        );
    } finally {
        onBusy(false);
    }

    let failed = 0;
    // Building/inserting runs after the upload phase: generateJSON (malformed
    // clipboard HTML) or insertContentAt (schema mismatch) can throw, and the
    // caller invokes this unawaited — so contain failures and surface a notice
    // rather than leaving an unhandled rejection.
    try {
        const extensions = editor.extensionManager.extensions;
        const content: JSONContent[] = [];
        let group: UploadedImage[] = [];
        let imageIndex = 0;

        const flushGroup = (): void => {
            if (group.length > 0) {
                content.push(...toNodes(group));
                group = [];
            }
        };

        for (const segment of segments) {
            if (segment.kind === 'image') {
                const uploaded = uploads[imageIndex];
                imageIndex += 1;
                if (uploaded) group.push(uploaded);
                else failed += 1;
                continue;
            }
            // A break or text run ends the current same-line image group.
            flushGroup();
            if (segment.kind === 'html') {
                const doc = generateJSON(segment.html, extensions);
                if (Array.isArray(doc.content)) content.push(...(doc.content as JSONContent[]));
            }
        }
        flushGroup();

        if (content.length > 0) editor.commands.insertContentAt(insertAt, content);
    } catch {
        onNotice("Couldn't insert the pasted content. Please try again.");
        return;
    }

    if (failed > 0) {
        const anySuccess = uploads.some(Boolean);
        onNotice(
            anySuccess
                ? `Couldn't import ${failed} of ${imageSrcs.length} pasted images.`
                : `Couldn't import the pasted image${imageSrcs.length > 1 ? 's' : ''}. Try copying the image on its own.`
        );
    }
}
